/**
 * Largest-remainder allocation (#630 §1).
 *
 * The Usage spend card shows a headline total plus two breakdowns, and all
 * three MUST reconcile to the cent. Rounding each part independently produces
 * `$14,001` of parts under a `$14,002` headline — the exact class of bug the
 * redesign exists to remove. Instead: floor the total, then hand out the
 * leftover units one at a time to the parts with the largest fractional
 * remainder. The result is guaranteed to sum to `floor(total)`.
 *
 * `shares` are relative weights (they need not sum to 1 — they are normalised
 * internally), one per part. The return array is parallel to `shares`, each an
 * integer, summing exactly to `Math.floor(total)`.
 *
 * @param total whole-unit total to distribute (e.g. dollars); floored first
 * @param shares non-negative relative weights, one per part
 * @returns integer parts, same length as `shares`, summing to `floor(total)`
 */
export function allocate(total: number, shares: number[]): number[] {
  const t = Math.floor(total);
  const sum = shares.reduce((a, b) => a + b, 0);
  if (t <= 0 || sum <= 0) return shares.map(() => 0);

  // Ideal (fractional) allocation against the FLOORED total. Summing raw gives
  // exactly `t` (t * sum / sum), so flooring loses at most `shares.length - 1`
  // whole units — the remainder we redistribute below.
  const raw = shares.map((s) => (t * s) / sum);
  const out = raw.map(Math.floor);
  let rem = t - out.reduce((a, b) => a + b, 0);

  // Descending fractional part; ties keep input order (stable).
  const order = raw
    .map((v, i) => [v - Math.floor(v), i] as const)
    .sort((a, b) => b[0] - a[0]);

  for (let k = 0; rem > 0; k++, rem--) {
    out[order[k % order.length][1]] += 1;
  }
  return out;
}
